<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout

========================================================================================== -->

<template>
	<div class="layout--main" :class="[layoutTypeClass, navbarClasses, footerClasses, { 'app-page': isAppPage }]">
		<vx-tour :steps="steps" v-if="!disableThemeTour && windowWidth >= 1200 && mainLayoutType === 'vertical'" />

		<the-customizer
			v-if="!disableCustomizer"
			:footerType="footerType"
			:hideScrollToTop="hideScrollToTop"
			:navbarType="navbarType"
			:navbarColor="navbarColor"
			:routerTransition="routerTransition"
			@toggleHideScrollToTop="toggleHideScrollToTop"
			@updateFooter="updateFooter"
			@updateNavbar="updateNavbar"
			@updateNavbarColor="updateNavbarColor"
			@updateRouterTransition="updateRouterTransition"
		/>

		<v-nav-menu :navMenuItems="navMenuItems" :logo="navMenuLogo" title="" parent=".layout--main" />

		<div id="content-area" :class="[contentAreaClass, { 'show-overlay': bodyOverlay }]">
			<div id="content-overlay"></div>

			<div v-if="showNavbar">
				<!-- Navbar -->
				<template v-if="mainLayoutType === 'horizontal' && windowWidth >= 1200">
					<the-navbar-horizontal :navbarType="navbarType" :logo="navMenuLogo" :class="[{ 'text-white': isNavbarDark && !isThemeDark }, { 'text-base': !isNavbarDark && isThemeDark }]" />

					<div style="height: 62px" v-if="navbarType === 'static'"></div>

					<h-nav-menu :class="[{ 'text-white': isNavbarDark && !isThemeDark }, { 'text-base': !isNavbarDark && isThemeDark }]" :navMenuItems="navMenuItems" />
				</template>

				<template v-else>
					<the-navbar-vertical :navbarColor="navbarColor" :class="[{ 'text-white': isNavbarDark && !isThemeDark }, { 'text-base': !isNavbarDark && isThemeDark }]" />
				</template>
				<!-- /Navbar -->
			</div>

			<div class="content-wrapper">
				<div :class="['router-view', { 'px-0': !showNavbar }]">
					<div :class="['router-content', { 'mt-0': !showNavbar }]">
						<transition :name="routerTransition">
							<div v-if="($route.meta.breadcrumb || routeTitle) && showNavbar" class="router-header flex flex-wrap items-center mb-0 md:mb-6">
								<div class="content-area__heading" :class="{ 'pr-4 border-0 md:border-r border-solid border-grey-light': $route.meta.breadcrumb }">
									<h2 class="mb-1">{{ $t(routeTitle) }}</h2>
								</div>

								<!-- BREADCRUMB -->
								<vx-breadcrumb class="ml-4 md:block hidden" v-if="$route.meta.breadcrumb" :route="$route" />
							</div>
						</transition>

						<div class="content-area__content">
							<back-to-top bottom="5%" visibleoffset="500" v-if="!hideScrollToTop">
								<vs-button color="primary" class="icon-white" icon-pack="nucleo" icon="nucleo-up-arrow"> </vs-button>
							</back-to-top>

							<transition :name="routerTransition" mode="out-in">
								<router-view :key="$route.path" @changeRouteTitle="changeRouteTitle"></router-view>
							</transition>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from "vue";
import moment from "moment-timezone";
import themeConfig from "@/../themeConfig";

import BackToTop from "vue-backtotop";
import TheCustomizer from "@/layouts/components/customizer/TheCustomizer.vue";
import navMenuItems from "@/layouts/components/vertical-nav-menu/navMenuItems";
import TheNavbarVertical from "@/layouts/components/navbar/TheNavbarVertical.vue";
import VNavMenu from "@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue";
import TheNavbarHorizontal from "@/layouts/components/navbar/TheNavbarHorizontal.vue";
import HNavMenu from "@/layouts/components/horizontal-nav-menu/HorizontalNavMenu.vue";

// register global components
import NotiIcon from "@/components/noti-icon/NotiIcon.vue";
import CbsUpload from "@/components/cbs-upload/CbsUpload.vue";
import CbsTextarea from "@/components/cbs-textarea/CbsTextarea.vue";
import CbsPagination from "@/components/cbs-pagination/CbsPagination.vue";
import CbsDatePicker from "@/components/cbs-date-picker/CbsDatePicker.vue";
import CbsMaskedInput from "@/components/cbs-masked-input/CbsMaskedInput.vue";
import CbsRelationIcon from "@/components/cbs-relation-icon/CbsRelationIcon.vue";
import CbsDownloadButton from "@/components/cbs-download-button/CbsDownloadButton.vue";
import CbsDateRangePicker from "@/components/cbs-date-range-picker/CbsDateRangePicker.vue";
import CbsRangeMaskedInput from "@/components/cbs-range-masked-input/CbsRangeMaskedInput.vue";
import CbsRelationsHumanroles from "@/components/cbs-relations-humanroles/CbsRelationsHumanroles.vue";

Vue.component(CbsUpload.name, CbsUpload);
Vue.component(CbsTextarea.name, CbsTextarea);
Vue.component(CbsDatePicker.name, CbsDatePicker);
Vue.component(CbsRangeMaskedInput.name, CbsRangeMaskedInput);
Vue.component(CbsMaskedInput.name, CbsMaskedInput);
Vue.component(CbsDownloadButton.name, CbsDownloadButton);
Vue.component(CbsDateRangePicker.name, CbsDateRangePicker);
Vue.component(CbsRelationsHumanroles.name, CbsRelationsHumanroles);
Vue.component(CbsRelationIcon.name, CbsRelationIcon);
Vue.component(NotiIcon.name, NotiIcon);
Vue.component(CbsPagination.name, CbsPagination);

export default {
	components: {
		BackToTop,
		HNavMenu,
		TheCustomizer,
		TheNavbarHorizontal,
		TheNavbarVertical,
		VNavMenu
	},
	data() {
		return {
			disableCustomizer: themeConfig.disableCustomizer,
			disableThemeTour: themeConfig.disableThemeTour,
			footerType: themeConfig.footerType || "static",
			hideScrollToTop: themeConfig.hideScrollToTop,
			isNavbarDark: false,
			navbarColor: themeConfig.navbarColor || "#fff",
			navbarType: themeConfig.navbarType || "floating",
			navMenuItems: navMenuItems(),
			navMenuLogo: require(`@/assets/images/logo/logo.svg`),
			routerTransition: themeConfig.routerTransition || "none",
			routeTitle: this.$route.meta.pageTitle,
			steps: [
				{
					target: "#btnVNavMenuMinToggler",
					content: "Toggle Collapse Sidebar."
				},
				{
					target: ".vx-navbar__starred-pages",
					content: "Create your own bookmarks. You can also re-arrange them using drag & drop."
				},
				{
					target: ".i18n-locale",
					content: "You can change language from here."
				},
				{
					target: ".navbar-fuzzy-search",
					content: "Try fuzzy search to visit pages in flash."
				},
				{
					target: ".customizer-btn",
					content: "Customize template based your preference",
					params: {
						placement: "left"
					}
				}
			],
			isLoadingInitData: false
		};
	},
	watch: {
		$route() {
			this.routeTitle = this.$route.meta.pageTitle;
		},
		isThemeDark(val) {
			const color = this.navbarColor === "#fff" && val ? "#10163a" : "#fff";
			this.updateNavbarColor(color);
		},
		"$store.state.mainLayoutType"(val) {
			this.setNavMenuVisibility(val);
			this.disableThemeTour = true;
		},
		windowWidth(val) {
			if (val < 1200) this.disableThemeTour = true;
		},
		verticalNavMenuWidth() {
			this.disableThemeTour = true;
		}
	},
	computed: {
		bodyOverlay() {
			return this.$store.state.bodyOverlay;
		},
		contentAreaClass() {
			if (this.mainLayoutType === "vertical") {
				if (this.verticalNavMenuWidth === "default") return "content-area-reduced";
				else if (this.verticalNavMenuWidth === "reduced") return "content-area-lg";
			}
			// else if(this.mainLayoutType === "boxed") return "content-area-reduced"
			else return "content-area-full";
		},
		footerClasses() {
			return {
				"footer-hidden": this.footerType === "hidden",
				"footer-sticky": this.footerType === "sticky",
				"footer-static": this.footerType === "static"
			};
		},
		isAppPage() {
			return !!this.$route.path.includes("/apps/");
		},
		isThemeDark() {
			return this.$store.state.theme === "dark";
		},
		layoutTypeClass() {
			return `main-${this.mainLayoutType}`;
		},
		mainLayoutType() {
			return this.$store.state.mainLayoutType;
		},
		navbarClasses() {
			return {
				"navbar-hidden": this.navbarType === "hidden",
				"navbar-sticky": this.navbarType === "sticky",
				"navbar-static": this.navbarType === "static",
				"navbar-floating": this.navbarType === "floating"
			};
		},
		verticalNavMenuWidth() {
			return this.$store.state.verticalNavMenuWidth;
		},
		windowWidth() {
			return this.$store.state.windowWidth;
		},
		requestTypes() {
			return this.$store.state.requestTypes || [];
		},
		showNavbar() {
			return this.$store.state.showNavbar;
		}
	},
	methods: {
		changeRouteTitle(title) {
			this.routeTitle = title;
		},
		updateNavbar(val) {
			if (val === "static") this.updateNavbarColor("#fff");
			this.navbarType = val;
		},
		updateNavbarColor(val) {
			this.navbarColor = val;
			this.isNavbarDark = val !== "#fff";
		},
		updateFooter(val) {
			this.footerType = val;
		},
		updateRouterTransition(val) {
			this.routerTransition = val;
		},
		setNavMenuVisibility(layoutType) {
			if ((layoutType === "horizontal" && this.windowWidth >= 1200) || (layoutType === "vertical" && this.windowWidth < 1200)) {
				this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", false);
				this.$store.dispatch("updateVerticalNavMenuWidth", "no-nav-menu");
			} else {
				this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", true);
			}
		},
		toggleHideScrollToTop(val) {
			this.hideScrollToTop = val;
		},
		initMenuItems() {
			this.navMenuItems = navMenuItems({
				departments: this.$store.state.departments,
				deviceCategories: this.$store.state.deviceCategories
			});
		},
		async loadInitData() {
			try {
				if (!this.$store.state.isLoadedInitData) {
					this.isLoadingInitData = true;
					this.$vs.loading();
					await Promise.all([this.$store.dispatch("fetchRequestTypes"), this.$store.dispatch("fetchDepartments", {})]);
					this.$store.commit("SET_IS_LOADED_INIT_DATA", true);
					this.initMenuItems();
					this.isLoadingInitData = false;
				}
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		}
	},
	created() {
		const color = this.navbarColor === "#fff" && this.isThemeDark ? "#10163a" : this.navbarColor;
		this.updateNavbarColor(color);
		this.setNavMenuVisibility(this.$store.state.mainLayoutType);
		this.unSubscribe = this.$store.subscribe(mutation => {
			if (mutation.type === "auth/UPDATE_USER_SETTINGS") {
				if (mutation.payload.timezone && mutation.payload.timezone != "") {
					moment.tz.setDefault(mutation.payload.timezone);
				}
			}
		});
	},
	mounted() {
		this.loadInitData();
	},
	updated() {
		if (!this.$store.state.isLoadedInitData && !this.isLoadingInitData) {
			this.loadInitData();
		}
	},
	beforeDestroy() {
		this.$store.commit("SET_IS_LOADED_INIT_DATA", false);
		this.unSubscribe();
	}
};
</script>

<style lang="scss">
.icon-white i {
	color: white;
}
</style>
